import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  Container,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ThemeProvider,
} from '@mui/material';
import { collection, collectionGroup, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import AdminNavbar from '../../components/AdminNavbar';
import { addDays, format, parse } from 'date-fns';
import { generateTimeSlots } from '../../components/TimeSlots';
import dayjs from 'dayjs';
import SlotEditModal from '../../components/SlotEditModal';
import { defaultStartTime, defaultEndTime, defaultSlotSize } from 'shared';
import { useLocations } from '../../components/LocationSelect';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { convertTimeZone, getCalendarOverwrites, getUserTimeZone } from '../../helpers';


// Styled components
const GridCell = styled(Box)(({ theme, isSelected }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  height: '60px',
  cursor: 'pointer',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
  backgroundColor: isSelected ? theme.palette.primary.light : 'transparent',
  '&:hover': {
    backgroundColor: isSelected 
      ? theme.palette.primary.light 
      : theme.palette.action.hover,
  },
}));

const TimeHeader = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  minWidth: '80px',
}));


export const timeSlotPurchases = (purchases, time, timeZone) => {
  if (!purchases) return [];
  return purchases.filter(p => 
    format(convertTimeZone(p.selectedDateTime.toDate(), timeZone ?? getUserTimeZone()), 'HH:mm') === time
  );
}

export const usePurchases = ({location, selectedDate}) => {
  const [purchases, setPurchases] = useState([]);
  useEffect(() => {

    const startDate = selectedDate ? new Date(selectedDate) : new Date();
    startDate.setHours(0, 0, 0, 0);
    const endDate = selectedDate ? new Date(selectedDate) : new Date();
    endDate.setHours(23, 59, 59, 999);

    const purchasesQuery = query(collection(db, 'ticketPurchases'), 
    where('selectedDateTime', '>=', startDate), 
    where('selectedDateTime', '<=', endDate),
    where('ticketLocation', '==', location),
    where('status', 'in', ['paid', 'used']))
    
    // Query for purchases
    const unsubscribe = onSnapshot(purchasesQuery, (querySnapshot) => {
      const purchases = [];
      querySnapshot.forEach((doc) => {
        purchases.push({ ...doc.data(), id: doc.id });
      });
      setPurchases(purchases); // Log or handle today's purchases as needed
      console.log(purchases);
    });

    return () => unsubscribe(); // Cleanup subscription on unmount
  }, [location, selectedDate]);

  return purchases;
}

const Schedule = () => {
  const theme = useTheme();
  const [location, setLocation] = useState('sydney');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();

  const purchases = usePurchases({location, selectedDate});
  const [openModal, setOpenModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const locations = useLocations();

  const selectedLocation = locations.find(l => l.id === location);

  const [overwrites, setOverwrites] = useState([]);

  useEffect(() => {
    getCalendarOverwrites(location, true).then(setOverwrites);
  }, [location]);

  const customHours = overwrites.find(d => d.date === dayjs(selectedDate)?.format('YYYY-MM-DD') && d.time);

  const startTime = customHours?.time?.from || selectedLocation?.hours?.[dayOfWeek]?.from || defaultStartTime;
  const endTime = customHours?.time?.to || selectedLocation?.hours?.[dayOfWeek]?.to || defaultEndTime;

  const timeSlots = generateTimeSlots({
    startTime,
    endTime,
    slotSize: defaultSlotSize,
  });

  const Slot = ({purchase, time}) => {
    const handleCellClick = () => {
      setSelectedPurchase({...purchase, time});
      setOpenModal(true);
    };
  
    return (
      <GridCell
      onClick={() => handleCellClick()}
    >
      {purchase.groupSize}x {purchase.user.firstName} {purchase.user.lastName}
    </GridCell>
    );
  
  }

  return (
    <>
    <AdminNavbar />
    <ThemeProvider theme={theme}>
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    <Box sx={{ flexGrow: 1, marginTop: 2, marginBottom: 2 }}>
        <FormControl>
          <InputLabel>Location</InputLabel>
          <Select
            value={location}
            label="Location"
            onChange={(event) => {
              setLocation(event.target.value)
        
            }}
            style={{ minWidth: "100%" }}
          >
            {
              locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)
            }
          </Select>
        </FormControl> 
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              closeOnSelect={true}
              label="For date"
              inputFormat="yyyy/MM/dd"
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              renderInput={(params) => <TextField style={{marginLeft: 10}} {...params} />}
            />
          </LocalizationProvider>
      </Box>

      {/* <DndContext> */}
      {selectedLocation && <Paper elevation={3} sx={{ overflow: 'auto', maxHeight: '80vh' }}>
        <Box sx={{ minWidth: 800 }}>
          {/* Header row with time slots */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: `repeat(${timeSlots.length}, minmax(120px, 1fr))` 
          }}>
            {timeSlots.map((time, timeIndex) => (
              <Box key={timeIndex} sx={{ display: 'flex', flexDirection: 'column' }}>
              <TimeHeader>
                <Typography variant="caption" style={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                }}>{time}</Typography>
              </TimeHeader>
                 {timeSlotPurchases(purchases, time, selectedLocation.timeZone).map((purchase) => {
                  return (
                   <Slot key={purchase.id} time={time} purchase={purchase} />  
                  );
                })}
            </Box>

            ))}
          </Box>
        </Box>
        </Paper>
      }
      {/* </DndContext> */}
      </Container>
      {selectedPurchase && selectedLocation && <SlotEditModal purchases={purchases} timeZone={selectedLocation.timeZone} timeSlots={timeSlots} selectedPurchase={selectedPurchase} open={openModal} onClose={() => setOpenModal(false)} />}  
    </ThemeProvider>
    </>
  );
};

export default Schedule;
